import i18n from '@/lang/i18n'
import { ColDef } from '@ag-grid-enterprise/all-modules'
import { initialCrudModelBaseState, CrudModelBase } from '@/shared/models/cruds.models'
import { AgGridColumnType } from '@/shared/models/ag-grid.model'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'
import { Nullable } from '@/shared/types/app.types'
import { CommonPossibleAssayResult } from '@/api/resources/common/commonPossibleAssayResult/common-possible-assay-result-services.models'

interface AssayStatusCode {
    translationKey: string
}

export interface AssayStatus {
    code: AssayStatusCode
    name: string
}

export enum AssayGroupType {
    OTHER = 'OTHER',
    CYTOLOGY = 'CYTOLOGY'
}

export interface AssayEvaluationResultCustomData {
    id?: number
    fieldName: string
    value: string
}

export interface AssaysEvaluationResult {
    id: Nullable<number>
    comment: string
    customData: Nullable<AssayEvaluationResultCustomData[]>
    percentage: Nullable<number>,
    possibleAssayResult: Nullable<CommonPossibleAssayResult>
}

export interface AssaysEvaluationStatus {
    code: AssayStatusCode
    name: string
}

export interface AssayGroup {
    code: string
    id: number
    name: string
    type: AssayGroupType
}

export interface EvaluationAssay {
    id?: number
    active: boolean
    assaygroup: AssayGroup
    assaygroupLaboratoryName: string
    code: string
    externalId: string
    name: string
}

export interface AssaysEvaluationAssayGridResponse {
    assay: EvaluationAssay
    assayStatus: AssayStatus
    label: string
    portionId: number
    result: Nullable<AssaysEvaluationResult>
    status: AssaysEvaluationStatus
    subportionAccredited: boolean
    subportionId: number
    subportionNumber: string
    subportionVisibleInReport: boolean
}

export interface AssayEvaluationAssayResultRequests {
    comment?: Nullable<string>
    customData?: Nullable<AssayEvaluationResultCustomData[]>
    percentage: Nullable<number>
    qualitativeResultId?: Nullable<number>
    subportionAccredited: boolean
    subportionVisibleInReport: boolean
}
export interface AssayEvaluationAssayResultCreateRequest {
    portionId: number
    subportionId: number
    requests: AssayEvaluationAssayResultRequests
}

export interface AssayEvaluationAssayResultUpdateRequest {
    id: Nullable<number>
    portionId: number
    subportionId: number
    updateRequests: AssayEvaluationAssayResultRequests
}

interface AssayResultsDeleteRequestEntityIdentifier {
    id: number
    portionId: number
    subportionId: number
}

export interface AssayResultsRequestDelete {
    entityIdentifiers: AssayResultsDeleteRequestEntityIdentifier[]
}

export const columnsDefAssaysEvaluationsGrid =
    (accreditationAssayActive: boolean): ColDef[] => {
        const columns: ColDef[] = [
            { type: AgGridColumnType.CUSTOM_CHECKBOX },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('diagnosis.grid.subportionGrid.subportionLabel'),
                field: 'label',
                minWidth: 170
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('diagnosis.grid.subportionGrid.assay'),
                minWidth: 140,
                field: 'assay.name'
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('diagnosis.grid.subportionGrid.assayGroup'),
                minWidth: 140,
                field: 'assay.assaygroup.name'
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('diagnosis.grid.subportionGrid.result'),
                suppressSizeToFit: true,
                minWidth: 140,
                field: 'result.possibleAssayResult.name'
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('diagnosis.grid.subportionGrid.percentage'),
                headerTooltip: i18n.tc('diagnosis.grid.subportionGrid.percentage'),
                minWidth: 60,
                field: 'result.percentage',
                filter: false,
                headerComponentParams: {
                    template: buildIconHeaderTemplate('fas fa-percent')
                }
            },
            {
                type: AgGridColumnType.CHECK_ICON,
                headerName: i18n.tc('diagnosis.grid.subportionGrid.pathologyReport'),
                minWidth: 160,
                field: 'subportionVisibleInReport'
            },
            {
                type: AgGridColumnType.CHECK_ICON,
                headerName: i18n.tc('diagnosis.grid.subportionGrid.additionalInformation'),
                minWidth: 160,
                field: 'hasAdditionalInformation'
            }
        ]
        if (accreditationAssayActive) {
            columns.push({
                type: 'checkIconRenderer',
                headerName: i18n.tc('diagnosis.grid.subportionGrid.enacAccreditation'),
                minWidth: 160,
                flex: 1,
                field: 'subportionAccredited'
            })
        }
        return columns
    }

export type AssaysEvaluationsGridState = CrudModelBase<any>
export const initialAssaysEvaluationsGridState =
    (accreditationAssayActive: boolean): AssaysEvaluationsGridState => ({
        ...initialCrudModelBaseState(),
        columnsDef: columnsDefAssaysEvaluationsGrid(accreditationAssayActive),
        visibleSidePanel: false
    })
